import { Currency } from 'module/api/account/types';
import { HttpClient } from 'api/http-client';
import { ProjectFinancialsContractType } from './other-types';
import { LinkedResource } from './other-types';
import { IntegrationField } from './types';
import {
  ChildResourcesResponse,
  ContactFieldSyncDetailConfig,
  CreateAutomationPayload,
  ErrorLogs,
  ErrorLogsResponse,
  MappableSalesforceFieldsResponse,
  SaveContactFieldSyncPayload,
  SaveContactFieldSyncResponse,
  SalesforceActions,
  SalesforceAutomation,
  SalesforceConfig,
  SalesforceFieldMapping,
  SalesforceIntegrationProvider,
  SalesforceObject,
  SalesforceResourceMapping,
  SalesforceResourceTypesByCategory,
  SalesforceSettingsMeta,
  SfInstallations,
  UpdateAutomationPayload,
} from './types';

const base = '/integrations/salesforce';

interface GetAuthDetailsPayload {
  params?: Record<any, any>;
}

class SalesforceHTTPClient extends HttpClient {
  public getAuthDetails = ({
    params,
  }: GetAuthDetailsPayload): Promise<SalesforceIntegrationProvider> =>
    this.get<SalesforceIntegrationProvider>(
      '/integration-providers/salesforce/meta',
      {
        params,
      },
    );

  public disconnectIntegration = (integrationId: number): Promise<void> =>
    this.delete<void>(`${base}/${integrationId}`);

  public getIntegrationConfig = (): Promise<SalesforceConfig[]> =>
    this.get<SalesforceConfig[]>(base);

  public getIntegrationConfigById = (
    integrationId: number,
  ): Promise<SalesforceConfig> =>
    this.get<SalesforceConfig>(`${base}/${integrationId}`);

  /**
   * @deprecated
   */
  public getTriggerObjects = (
    integrationId: number,
  ): Promise<SalesforceObject[]> =>
    this.get<SalesforceObject[]>(
      `${base}/${integrationId}/automations-builder/trigger-objects`,
    );

  public getTriggerObjectsV2 = (
    integrationId: number,
  ): Promise<SalesforceObject[]> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.get<SalesforceObject[]>(
      `${base}/${integrationId}/automations-builder/trigger-objects`,
      { baseURL: v2BaseURL },
    );
  };

  /**
   * @deprecated
   */
  public getAutomationTriggerFields = (
    integrationId: number,
    salesforceObject: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/trigger-condition-fields`,
    );

  public getAutomationTriggerFieldsV2 = (
    integrationId: number,
    salesforceObject: string,
  ): Promise<IntegrationField[]> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.get<IntegrationField[]>(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/trigger-condition-fields`,
      { baseURL: v2BaseURL },
    );
  };

  /**
   * @deprecated
   */
  public getAutomationMatchFields = (
    integrationId: number,
    salesforceObject: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/matching-condition-fields`,
    );

  public getAutomationMatchFieldsV2 = (
    integrationId: number,
    salesforceObject: string,
  ): Promise<IntegrationField[]> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.get<IntegrationField[]>(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/matching-condition-fields`,
      { baseURL: v2BaseURL },
    );
  };

  /**
   * @deprecated
   */
  public getSalesforceActionFields = (
    integrationId: number,
    salesforceObject: string,
    actionType: SalesforceActions,
    triggerObjectParentPlaceholder?: string,
  ): Promise<IntegrationField[]> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.get<IntegrationField[]>(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/action-objects/${actionType}/action-input-fields`,
      {
        baseURL: v2BaseURL,
        params: triggerObjectParentPlaceholder
          ? { triggerObjectParentPlaceholder }
          : undefined,
      },
    );
  };

  public getSalesforceActionFieldsV2 = (
    integrationId: number,
    salesforceObject: string,
    actionType: SalesforceActions,
    triggerObjectParentPlaceholder?: string,
  ): Promise<IntegrationField[]> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.get<IntegrationField[]>(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/action-objects/${actionType}/action-input-fields`,
      {
        baseURL: v2BaseURL,
        params: triggerObjectParentPlaceholder
          ? { triggerObjectParentPlaceholder }
          : undefined,
      },
    );
  };

  /**
   * @deprecated
   */
  public getAutomations = (): Promise<SalesforceAutomation[]> => {
    return this.get('/automations', { baseURL: this.BASEURL });
  };

  public getAutomationsV2 = (): Promise<SalesforceAutomation[]> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.get('/automations', { baseURL: v2BaseURL });
  };

  /**
   * @deprecated
   */
  public getAutomationById = (
    automationId: number,
  ): Promise<SalesforceAutomation> => this.get(`/automations/${automationId}`);

  public getAutomationByIdV2 = (
    automationId: number,
  ): Promise<SalesforceAutomation> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.get(`/automations/${automationId}`, { baseURL: v2BaseURL });
  };

  /**
   * @deprecated - Use createAutomationV2 instead
   */
  public createAutomation = (
    payload: CreateAutomationPayload,
  ): Promise<SalesforceAutomation> => this.post('/automations', payload);


  public createAutomationV2 = (
    payload: CreateAutomationPayload,
  ): Promise<SalesforceAutomation> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.post(
      '/automations',
      { ...payload, automationType: 'CRM_V2' },
      { baseURL: v2BaseURL },
    );
  };

  /**
   * @deprecated
   */
  public updateAutomation = (
    payload: UpdateAutomationPayload,
  ): Promise<SalesforceAutomation> =>
    this.put(`/automations/${payload.automationId}`, payload);

  public updateAutomationV2 = (
    payload: UpdateAutomationPayload,
  ): Promise<SalesforceAutomation> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.put(`/automations/${payload.automationId}`, payload, { baseURL: v2BaseURL });
  };

  /**
   * @deprecated
   */
  public deleteAutomation = (automaionId: number): Promise<void> =>
    this.delete(`/automations/${automaionId}`);

  public deleteAutomationV2 = (automationId: number): Promise<void> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.delete(`/automations/${automationId}`, { baseURL: v2BaseURL });
  };

  /**
   * @deprecated
   */
  public getTemplatePlaceholders = (
    integrationId: string,
    templateId: any,
    salesforceObject: string | number | undefined,
  ): Promise<IntegrationField[]> =>
    this.get(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/dynamic-action-input-fields?templateId=${templateId}`,
    );

  public getTemplatePlaceholdersV2 = (
    integrationId: string,
    templateId: any,
    salesforceObject: string | number | undefined,
  ): Promise<IntegrationField[]> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.get(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/dynamic-action-input-fields?templateId=${templateId}`,
      { baseURL: v2BaseURL },
    );
  };

  /**
   * @deprecated
   */
  public getFinancialsFields = (
    integrationId: string,
    billingMethod: ProjectFinancialsContractType,
    currencyCode: Currency['currencyCode'],
    salesforceObject: string | number | undefined,
  ): Promise<IntegrationField[]> =>
    this.get(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/dynamic-action-input-fields?financialContractType=${billingMethod}&currencyCode=${currencyCode}`,
    );

  public getFinancialsFieldsV2 = (
    integrationId: string,
    billingMethod: ProjectFinancialsContractType,
    currencyCode: Currency['currencyCode'],
    salesforceObject: string | number | undefined,
  ): Promise<IntegrationField[]> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.get(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/dynamic-action-input-fields?financialContractType=${billingMethod}&currencyCode=${currencyCode}`,

      { baseURL: v2BaseURL },
    );
  };

  /**
   * @deprecated
   */
  public reorderAutomation = (
    automationId: number,
    automationNewOrder: number,
  ): Promise<SalesforceAutomation[]> =>
    this.put(`/automations/${automationId}/reorder`, {
      order: automationNewOrder,
    });

  public reorderAutomationV2 = (
    automationId: number,
    automationNewOrder: number,
  ): Promise<SalesforceAutomation[]> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.put(`/automations/${automationId}/reorder`, {
      order: automationNewOrder,
    }, { baseURL: v2BaseURL });
  };

  public getResourceMappings = (
    integrationId: number,
    nativeResourceType?: 'project' | 'company' | 'user',
  ): Promise<SalesforceResourceMapping[]> =>
    this.get<SalesforceResourceMapping[]>(
      `${base}/${integrationId}/resource-mapping-config`,
      {
        params: nativeResourceType ? { nativeResourceType } : undefined,
      },
    );

  public getExternalResourceTypes = (
    integrationId: number,
  ): Promise<SalesforceResourceTypesByCategory> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.get<SalesforceResourceTypesByCategory>(
      `${base}/${integrationId}/field-mapping-config-builder/external-resource-types`,
      { baseURL: v2BaseURL },
    );
  };

  public getExternalResourceFields = (
    integrationId: number,
    externalResourceType: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/field-mapping-config-builder/external-resource-types/${externalResourceType}/fields`,
    );

  public getNativeResourceTypes = (
    integrationId: number,
  ): Promise<SalesforceResourceMapping[]> =>
    this.get<SalesforceResourceMapping[]>(
      `${base}/${integrationId}/field-mapping-config-builder/native-resource-types`,
    );

  public getNativeResourceFields = (
    integrationId: number,
    nativeResourceType: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/field-mapping-config-builder/native-resource-types/${nativeResourceType}/fields`,
    );

  /**
   * @deprecated
   */
  public getMappableNativeResourceFields = (
    integrationId: number,
    nativeResourceType: string,
    externalResourceType: string,
    externalResourceFieldId: string,
    fieldMappingConfigId?: number, // Optional parameter
  ): Promise<IntegrationField[]> => {
    const params = new URLSearchParams({
      externalResourceType,
      externalFieldId: externalResourceFieldId,
    });

    // Only add fieldMappingConfigId if it exists (editing mode)
    if (fieldMappingConfigId !== undefined && fieldMappingConfigId !== null) {
      params.append('fieldMappingConfigId', fieldMappingConfigId.toString());
    }

    return this.get<IntegrationField[]>(
      `${base}/${integrationId}/field-mapping-config-builder/native-resource-types/${nativeResourceType}/mappable-fields?${params.toString()}`,
    );
  };

  public getMappableNativeResourceFieldsV2 = (
    integrationId: number,
    nativeResourceType: string,
    externalResourceType: string,
    externalResourceFieldId: string,
    fieldMappingConfigId?: number, // Optional parameter
  ): Promise<IntegrationField[]> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');
    const params = new URLSearchParams({
      externalResourceType,
      externalFieldId: externalResourceFieldId,
    });

    if (fieldMappingConfigId !== undefined && fieldMappingConfigId !== null) {
      params.append('fieldMappingConfigId', fieldMappingConfigId.toString());
    }

    return this.get<IntegrationField[]>(
      `${base}/${integrationId}/field-mapping-config-builder/native-resource-types/${nativeResourceType}/mappable-fields?${params.toString()}`,
      { baseURL: v2BaseURL },
    );
  };

  public createFieldMapping = (
    integrationId: number,
    resourceObjectId: number,
    payload: SalesforceFieldMapping,
  ): Promise<SalesforceFieldMapping> =>
    this.post(
      `${base}/${integrationId}/resource-mapping-config/${resourceObjectId}/field-mapping-config`,
      payload,
    );

  public deleteFieldMapping = (
    integrationId: number,
    resourceId: number,
    fieldMappingId: number,
  ): Promise<void> =>
    this.delete(
      `${base}/${integrationId}/resource-mapping-config/${resourceId}/field-mapping-config/${fieldMappingId}`,
    );

  public updateFieldMapping = (
    integrationId: number,
    resourceObjectId: number,
    fieldMappingId: number,
    payload: SalesforceFieldMapping,
  ): Promise<SalesforceFieldMapping> =>
    this.put(
      `${base}/${integrationId}/resource-mapping-config/${resourceObjectId}/field-mapping-config/${fieldMappingId}`,
      payload,
    );

  public createResourceObject = (
    integrationId: number,
    payload: Pick<
      SalesforceResourceMapping,
      | 'externalIntegrationType'
      | 'externalResourceType'
      | 'nativeResourceType'
      | 'syncType'
      | 'enabled'
    >,
  ): Promise<SalesforceResourceMapping> =>
    this.post(`${base}/${integrationId}/resource-mapping-config/`, payload);

  public deleteResourceObject = (
    integrationId: number,
    resourceId: number,
  ): Promise<void> =>
    this.delete(
      `${base}/${integrationId}/resource-mapping-config/${resourceId}`,
    );

  public linkSalesforceObject = (
    integrationId,
    payload,
  ): Promise<LinkedResource> =>
    this.post(`${base}/${integrationId}/api/sobject/resource-mapping`, payload);

  public getSalesforceObjectWithProject = (
    integrationId,
    externalResourceId,
    externalResourceType,
  ): Promise<any> =>
    this.get(
      `${base}/${integrationId}/api/sobject/${externalResourceType}/${externalResourceId}`,
    );

  public unLinkSalesforceObjectWithProject = (
    integrationId,
    resourceMappingId,
  ): Promise<SalesforceObject> =>
    this.delete(
      `${base}/${integrationId}/resource-mapping/${resourceMappingId}`,
    );

  public getSalesForceInstallations(
    integrationId: number,
  ): Promise<SfInstallations[]> {
    return this.get(`${base}/${integrationId}/app-installations`);
  }

  public updateSalesforceIntegrationSettings = (
    integrationId: number,
    payload: SalesforceSettingsMeta,
  ): Promise<SalesforceSettingsMeta> =>
    this.put(`${base}/${integrationId}/settings`, payload);

  public getErrorLogs = (
    integrationId: number,
    params?: {
      page?: number;
      limit?: number;
      startDate?: string;
      endDate?: string;
    },
  ): Promise<ErrorLogsResponse> =>
    this.get<ErrorLogsResponse>(`${base}/${integrationId}/error-logs`, {
      params,
    });

  public getSalesForceErrorLogs = (
    integrationId: string,
  ): Promise<ErrorLogs[]> =>
    this.get<ErrorLogs[]>(`/error-logs/query?integrationId=${integrationId}`);

  public getSalesForceErrorLogsInCSV = (
    integrationId: string,
  ): Promise<string> =>
    this.get<{ downloadUrl: string }>(
      `${base}/export?integrationId=${integrationId}`,
    ).then((response) => response.downloadUrl);

  public getChildResources = (
    integrationId: number,
    triggerObject: string,
    triggerObjectParentPlaceholder?: string,
  ): Promise<ChildResourcesResponse> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.get<ChildResourcesResponse>(
      `${base}/${integrationId}/automations-builder/trigger-objects/${triggerObject}/related-resources`,
      {
        baseURL: v2BaseURL,
        params: triggerObjectParentPlaceholder
          ? { triggerObjectParentPlaceholder }
          : undefined,
      },
    );
  };

  public getContactMandatoryFields = (
    integrationId: number,
    contactObject: string,
  ): Promise<IntegrationField[]> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.get<IntegrationField[]>(
      `${base}/${integrationId}/automations-builder/validation/contact-object/${contactObject}/mandatory-fields`,
      {
        baseURL: v2BaseURL,
      },
    );
  };

  /**
   * Get detailed contact field sync configuration for modal/drawer
   * Returns all mandatory fields with their mapping details
   * Called when user clicks "Done" button
   */
  public getContactFieldSyncDetail = (
    integrationId: number,
    contactObject: string,
  ): Promise<ContactFieldSyncDetailConfig> => {
    return this.get<ContactFieldSyncDetailConfig>(
      `${base}/${integrationId}/resource-mapping-config/contact/${contactObject}/mandatory-fields-config`,
    );
  };

  /**
   * Get mappable Salesforce fields for a specific Rocketlane field
   * Returns dropdown options for field mapping in contact sync modal
   * Called for each Rocketlane field to populate Salesforce field dropdown
   */
  public getMappableSalesforceFields = (
    integrationId: number,
    externalResourceType: string,
    nativeResourceType: string,
    nativeFieldName: string,
  ): Promise<MappableSalesforceFieldsResponse> => {
    const v2BaseURL = this.BASEURL.replace(/\/v1$/, '/v2');

    return this.get<MappableSalesforceFieldsResponse>(
      `${base}/${integrationId}/field-mapping-config-builder/external-resource-types/${externalResourceType}/mappable-fields`,
      {
        baseURL: v2BaseURL,
        params: {
          nativeResourceType,
          nativeFieldName,
        },
      },
    );
  };

  /**
   * Save/Update contact field sync configuration
   * Creates or updates field mappings based on resourceMappingConfigId/fieldMappingConfigId presence
   * Called when user clicks "Sync Field" button in contact sync modal
   */
  public saveContactFieldSync = (
    integrationId: number,
    payload: SaveContactFieldSyncPayload,
  ): Promise<SaveContactFieldSyncResponse> => {
    return this.post(
      `${base}/${integrationId}/resource-mapping-config/contact/mandatory-fields-config`,
      payload,
    );
  };
}

export const SalesforceAPI = new SalesforceHTTPClient();
